import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Analytics,
  PostAdd,
  Schedule,
  Campaign,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { chatbotAPI } from '../services/api';

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [attachedFile, setAttachedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Try to detect client_id from URL or location state
  const getClientId = () => {
    // Check URL params first
    if (params.clientId) {
      return parseInt(params.clientId);
    }
    // Check location state (from navigation)
    if (location.state?.clientId) {
      return parseInt(location.state.clientId);
    }
    // Check localStorage (if client was recently selected)
    const storedClientId = localStorage.getItem('selectedClientId');
    if (storedClientId) {
      return parseInt(storedClientId);
    }
    return null;
  };

  const quickActions = [
    {
      title: 'Analytics',
      description: 'View performance insights',
      icon: <Analytics />,
      action: 'Show me my analytics',
    },
    {
      title: 'Create Content',
      description: 'Generate post ideas',
      icon: <PostAdd />,
      action: 'Help me create content',
    },
    {
      title: 'Schedule',
      description: 'Plan your posts',
      icon: <Schedule />,
      action: 'When should I post?',
    },
    {
      title: 'Campaigns',
      description: 'Optimize strategies',
      icon: <Campaign />,
      action: 'How can I improve my campaigns?',
    },
  ];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (content) => {
    const hasContent = (content && content.trim()) || attachedFile;
    if (!hasContent) return;

    const textContent = (content && content.trim()) || '';
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: attachedFile
        ? textContent
          ? `Uploaded image: ${textContent}`
          : 'Uploaded image for caption'
        : textContent,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    const fileToSend = attachedFile;
    setAttachedFile(null);
    setIsTyping(true);

    // If user attached an image: generate caption from media and show "Create post"
    if (fileToSend) {
      try {
        const result = await chatbotAPI.generateCaptionFromMedia(
          fileToSend,
          textContent
        );
        const captionText = `**Title:** ${result.title}\n\n**Caption:** ${
          result.caption
        }\n\n**Hashtags:** ${(result.hashtags || [])
          .map((h) => `#${h}`)
          .join(' ')}`;
        const botResponse = {
          id: Date.now(),
          type: 'bot',
          content: `Here’s a caption for your image:\n\n${captionText}`,
          timestamp: new Date(),
          createPostDraft: {
            title: result.title,
            content: result.caption,
            hashtags: (result.hashtags || []).join(', '),
            media_url: result.media_url,
          },
        };
        setMessages((prev) => [...prev, botResponse]);
      } catch (error) {
        console.error('Caption-from-media error:', error);
        const detail = error.response?.data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : 'Couldn’t generate a caption. Check that the file is an image (JPEG, PNG, WebP, GIF) and OPENAI_API_KEY is set in .env; restart the server after changing .env.';
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: 'bot',
            content: msg,
            timestamp: new Date(),
          },
        ]);
      }
      setIsTyping(false);
      return;
    }

    // Check if this is a graphic creation request
    const isGraphicRequest =
      textContent.toLowerCase().includes('create') &&
      (textContent.toLowerCase().includes('graphic') ||
        textContent.toLowerCase().includes('design') ||
        textContent.toLowerCase().includes('image'));

    // Add a "creating" message if it's a graphic request
    if (isGraphicRequest) {
      const creatingMessage = {
        id: Date.now() + 0.5,
        type: 'bot',
        content:
          "🎨 **Creating your graphic now...**\nI'm generating a beautiful design based on your request. This will just take a moment!",
        timestamp: new Date(),
        isCreating: true,
      };
      setMessages((prev) => [...prev, creatingMessage]);
    }

    // Call real AI API with conversation history
    try {
      const conversationHistory = messages.map((msg) => ({
        type: msg.type,
        content: msg.content,
      }));

      const clientId = getClientId();
      const response = await chatbotAPI.sendMessage(
        textContent,
        null,
        conversationHistory,
        clientId // Pass client_id for context-aware responses
      );
      const botResponse = {
        id: Date.now(),
        type: 'bot',
        content: response.response,
        timestamp: new Date(),
        suggestions: response.suggestions || [],
        visuals: response.visuals || [],
        actions: response.actions || [],
        graphics: response.graphics || [],
      };

      // Remove the "creating" message and add the final response
      setMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.isCreating);
        return [...filtered, botResponse];
      });
    } catch (error) {
      console.error('Chatbot API error:', error);
      const botResponse = generateBotResponse(textContent);
      setMessages((prev) => {
        const filtered = prev.filter((msg) => !msg.isCreating);
        return [...filtered, botResponse];
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleAttachImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setAttachedFile(file);
    }
    e.target.value = '';
  };

  const generateBotResponse = (userInput) => {
    // Fallback response generation
    const responses = [
      "I understand you're asking about that. Let me help you with some insights based on your data.",
      "That's a great question! I can see from your analytics that you're doing well.",
      "I'd be happy to help you with that. What specific aspect would you like to focus on?",
    ];

    return {
      id: Date.now(),
      type: 'bot',
      content: responses[Math.floor(Math.random() * responses.length)],
      timestamp: new Date(),
      suggestions: [
        'Show me analytics',
        'Create content',
        'Schedule posts',
        'Campaign help',
      ],
    };
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleActionClick = (action) => {
    // Handle navigation actions
    switch (action.action) {
      case 'navigate_to_analytics':
        window.location.href = '/analytics';
        break;
      case 'navigate_to_posts':
        window.location.href = '/posts';
        break;
      case 'navigate_to_campaigns':
        window.location.href = '/campaigns';
        break;
      default:
        console.log('Action:', action);
    }
  };

  const renderMessage = (message) => (
    <Box
      key={message.id}
      sx={{
        display: 'flex',
        justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '70%',
          backgroundColor: message.isCreating
            ? 'rgba(136, 132, 216, 0.2)'
            : message.type === 'user'
            ? '#1976d2'
            : 'rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          p: 2,
          position: 'relative',
          border: message.isCreating ? '2px solid #8884d8' : 'none',
          animation: message.isCreating ? 'pulse 2s infinite' : 'none',
        }}
      >
        {message.type === 'bot' && (
          <Avatar
            sx={{
              width: 24,
              height: 24,
              position: 'absolute',
              top: -12,
              left: -12,
              backgroundColor: '#8884d8',
            }}
          >
            <ChatIcon sx={{ fontSize: 16 }} />
          </Avatar>
        )}

        <Typography
          variant="body1"
          sx={{
            color: message.type === 'user' ? 'white' : '#fff',
            mb: 1,
            whiteSpace: 'pre-wrap',
            fontStyle: message.isCreating ? 'italic' : 'normal',
            fontWeight: message.isCreating ? 'bold' : 'normal',
          }}
        >
          {message.content}
        </Typography>

        {/* Create post from caption draft */}
        {message.createPostDraft && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PostAdd />}
              onClick={() =>
                navigate('/posts', {
                  state: { fromChatbot: true, draft: message.createPostDraft },
                })
              }
              sx={{
                backgroundColor: '#58a6ff',
                color: '#fff',
                '&:hover': { backgroundColor: '#1f6feb' },
              }}
            >
              Create post
            </Button>
          </Box>
        )}

        {/* Render actions */}
        {message.actions && message.actions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
              Quick Actions:
            </Typography>
            <Grid container spacing={1}>
              {message.actions.map((action, index) => (
                <Grid item key={index}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleActionClick(action)}
                    sx={{
                      color: '#8884d8',
                      borderColor: '#8884d8',
                      '&:hover': {
                        borderColor: '#6664d8',
                        backgroundColor: 'rgba(136, 132, 216, 0.1)',
                      },
                    }}
                  >
                    {action.title}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Render suggestions */}
        {message.suggestions && message.suggestions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={1}>
              {message.suggestions.map((suggestion, index) => (
                <Grid item key={index}>
                  <Chip
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{
                      backgroundColor: 'rgba(136, 132, 216, 0.2)',
                      color: '#8884d8',
                      '&:hover': {
                        backgroundColor: 'rgba(136, 132, 216, 0.3)',
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            color:
              message.type === 'user' ? 'rgba(255, 255, 255, 0.7)' : '#999',
            display: 'block',
            mt: 1,
          }}
        >
          {message.timestamp.toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );

  if (isMinimized) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={() => setIsMinimized(false)}
          sx={{
            background: 'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
            color: 'white',
            width: 64,
            height: 64,
            boxShadow: '0 8px 25px rgba(88, 166, 255, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #79c0ff 0%, #58a6ff 100%)',
              boxShadow: '0 12px 35px rgba(88, 166, 255, 0.6)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: 'linear-gradient(135deg, #58a6ff, #f78166)',
              borderRadius: '50%',
              zIndex: -1,
              opacity: 0.3,
            },
          }}
        >
          <ChatIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          width: isMobile ? 'calc(100vw - 40px)' : 400,
          height: 600,
          zIndex: 1000,
        }}
      >
        <Paper
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#161b22',
            border: '1px solid rgba(48, 54, 61, 0.4)',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
          }}
        >
          {/* Clean Header */}
          <Box
            sx={{
              p: 2.5,
              borderBottom: '1px solid rgba(48, 54, 61, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#21262d',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  backgroundColor: '#58a6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}
              >
                <ChatIcon sx={{ fontSize: 18, color: '#ffffff' }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    fontWeight: 600,
                    fontSize: '1rem',
                    lineHeight: 1.2,
                  }}
                >
                  AI Assistant
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8b949e',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                  }}
                >
                  Ready to help
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setIsMinimized(true)}
              sx={{
                color: '#8b949e',
                width: 32,
                height: 32,
                '&:hover': {
                  backgroundColor: 'rgba(248, 81, 73, 0.1)',
                  color: '#f85149',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                  }}
                >
                  How can I help you today?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#8b949e',
                    mb: 4,
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  Choose a quick action or ask me anything
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gridTemplateRows: '1fr 1fr',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  {quickActions.map((action, index) => (
                    <Box
                      key={index}
                      sx={{
                        aspectRatio: '1',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(48, 54, 61, 0.4)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(88, 166, 255, 0.08)',
                          borderColor: 'rgba(88, 166, 255, 0.5)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                      onClick={() => handleSendMessage(action.action)}
                    >
                      <Box
                        sx={{
                          color: '#58a6ff',
                          fontSize: 24,
                          mb: 1,
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#f0f6fc',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          mb: 0.5,
                        }}
                      >
                        {action.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#8b949e',
                          fontSize: '0.7rem',
                          textAlign: 'center',
                          lineHeight: 1.2,
                        }}
                      >
                        {action.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#6e7681',
                      fontSize: '0.75rem',
                    }}
                  >
                    Or type your question below
                  </Typography>
                </Box>
              </Box>
            ) : (
              messages.map(renderMessage)
            )}

            {isTyping && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: '#8884d8',
                      mr: 1,
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: '#ccc' }}>
                    AI is thinking...
                  </Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(48, 54, 61, 0.3)',
              backgroundColor: '#21262d',
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleAttachImage}
                sx={{
                  color: attachedFile ? '#58a6ff' : '#8b949e',
                  '&:hover': {
                    color: '#58a6ff',
                    backgroundColor: 'rgba(88, 166, 255, 0.1)',
                  },
                }}
                title="Attach image for caption"
              >
                <ImageIcon />
              </IconButton>
              {attachedFile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#8b949e',
                    maxWidth: 80,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {attachedFile.name}
                </Typography>
              )}
              <TextField
                fullWidth
                variant="outlined"
                placeholder={
                  attachedFile
                    ? 'Optional: describe the image...'
                    : 'Type your message...'
                }
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(inputValue);
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#f0f6fc',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: 'rgba(48, 54, 61, 0.5)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(88, 166, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#58a6ff',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#8b949e',
                    opacity: 1,
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    padding: '10px 14px',
                  },
                }}
              />
              <IconButton
                onClick={() => handleSendMessage(inputValue)}
                disabled={(!inputValue.trim() && !attachedFile) || isTyping}
                sx={{
                  backgroundColor:
                    (inputValue.trim() || attachedFile) && !isTyping
                      ? '#58a6ff'
                      : 'transparent',
                  color:
                    (inputValue.trim() || attachedFile) && !isTyping
                      ? '#ffffff'
                      : '#8b949e',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  border: '1px solid rgba(48, 54, 61, 0.5)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      (inputValue.trim() || attachedFile) && !isTyping
                        ? '#1f6feb'
                        : 'rgba(88, 166, 255, 0.1)',
                    borderColor: '#58a6ff',
                  },
                  '&:disabled': {
                    backgroundColor: 'transparent',
                    color: '#6e7681',
                    borderColor: 'rgba(48, 54, 61, 0.3)',
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default Chatbot;
